import React, { FC, ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useBeforeunload } from 'react-beforeunload';
import OvVisuallyHidden from '../atoms/OvVisuallyHidden';
import OvVideoStagingDialog from './OvVideoStagingDialog';
import { VideoStagingContext } from '../../../context/VideoStagingContext';
import { VideoStagingContextValue } from '../../../common/model/type/video-staging.types';
import { getStagingBlocker } from '../../../common/utils/video-staging-utils';
import { getAxiosErrorMessage } from '../../../common/utils/helpers';
import { getHttpStatus } from '../../../common/utils/http-status';
import { invalidateVideoQueries } from '../../../common/utils/video-query-cache';
import {
  getNewestFirstSearch,
  getVideoLibraryPath,
  isVideoLibraryPath,
} from '../../../common/utils/video-route-utils';
import { useAdminVideoMutations } from '../../../hooks/use-admin-video-mutations';
import { useVideoPermissions } from '../../../hooks/use-video-permissions';
import { useVideoStaging } from '../../../hooks/use-video-staging';
import { useVideoUploads } from '../../../hooks/use-video-uploads';

interface OvVideoStagingProviderProps {
  children: ReactNode;
}

const OvVideoStagingProvider: FC<OvVideoStagingProviderProps> = ({
  children,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  // Read when a request settles, which can be after the user has navigated.
  const latestLocation = useRef(location);
  latestLocation.current = location;
  const staging = useVideoStaging();
  const { create } = useAdminVideoMutations();
  const { enqueue, expandTray } = useVideoUploads();
  const { canCreate, canUpdate } = useVideoPermissions();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [returnSearch, setReturnSearch] = useState('');

  useBeforeunload(
    staging.staged.length ? (event) => event.preventDefault() : undefined
  );

  const blocker = getStagingBlocker(staging.staged, canCreate && canUpdate);
  const isUnknownOutcome =
    create.isError && getHttpStatus(create.error) === undefined;

  const openDialog = (files: File[] = []) => {
    if (files.length) {
      staging.addFiles(files);
    }

    setAnnouncement('');
    setIsDialogOpen(true);
  };

  const discard = () => {
    staging.reset();
    create.reset();
    setIsDialogOpen(false);
  };

  const showNewestFirst = () => {
    const { pathname, search } = latestLocation.current;

    if (isVideoLibraryPath(pathname)) {
      navigate(getVideoLibraryPath(getNewestFirstSearch(search)), {
        replace: true,
      });
    }

    setReturnSearch(getNewestFirstSearch);

    invalidateVideoQueries(queryClient);
  };

  const editInBulk = (librarySearch: string) => {
    setReturnSearch(librarySearch);
    setIsDialogOpen(false);
  };

  const submit = (onSuccess?: () => void) => {
    if (blocker.kind !== 'none' || create.isPending) {
      return;
    }

    const submitted = staging.staged;

    create.mutate(staging.requests, {
      onSuccess: (created) => {
        enqueue(
          created.map((upload, index) => ({
            ...upload,
            file: submitted[index].file,
            contentType: submitted[index].contentType,
          }))
        );
        staging.reset();
        create.reset();
        setIsDialogOpen(false);
        expandTray();
        setAnnouncement(t('videos.staging.uploadsStarted'));
        onSuccess?.();
      },
      onError: (error) => {
        if (getHttpStatus(error) === undefined) {
          showNewestFirst();
        }
      },
    });
  };

  const getSubmitError = () => {
    if (!create.isError) {
      return null;
    }

    return isUnknownOutcome ? 'unknown' : 'failed';
  };

  const value: VideoStagingContextValue = {
    staged: staging.staged,
    rejected: staging.rejected,
    duplicates: staging.duplicates,
    isEmpty: !staging.staged.length && !staging.rejected.length,
    isDialogOpen,
    isSubmitting: create.isPending,
    submitError: getSubmitError(),
    submitErrorDetail:
      create.isError && !isUnknownOutcome
        ? getAxiosErrorMessage(create.error)
        : null,
    blocker,
    returnPath: getVideoLibraryPath(returnSearch),
    addFiles: staging.addFiles,
    setTitle: staging.setTitle,
    setDescription: staging.setDescription,
    setOrientation: staging.setOrientation,
    setPlacement: staging.setPlacement,
    remove: staging.remove,
    openDialog,
    closeDialog: () => setIsDialogOpen(false),
    editInBulk,
    discard,
    submit,
  };

  return (
    <VideoStagingContext.Provider value={value}>
      {children}
      <OvVideoStagingDialog />
      <OvVisuallyHidden role="status">{announcement}</OvVisuallyHidden>
    </VideoStagingContext.Provider>
  );
};

export default OvVideoStagingProvider;
